import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { NotificationsService } from '../notifications.service';
import { NotificationDTO, metaFor } from '../notification.model';

const TOAST_DURATION_MS = 6000;
const LEAVE_ANIMATION_MS = 220;
const MAX_VISIBLE_TOASTS = 3;

interface ActiveToast {
  n: NotificationDTO;
  leaving: boolean;
  timeout: ReturnType<typeof setTimeout> | null;
}

/**
 * Pila de toasts arriba a la derecha (máx. 3). Se muestra un toast por cada
 * evento nuevo cuyo tipo tenga `toastEnabled`; suena un "ding" suave si
 * además el sonido no está silenciado. Auto-desaparecen a los ~6s (pausa al
 * pasar el ratón); click → navega y marca como vista.
 */
@Component({
  selector: 'app-notification-toasts',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslatePipe],
  templateUrl: './notification-toasts.component.html',
  styleUrls: ['./notification-toasts.component.css']
})
export class NotificationToastsComponent implements OnInit, OnDestroy {
  protected readonly svc = inject(NotificationsService);
  private readonly router = inject(Router);

  protected readonly toasts = signal<ActiveToast[]>([]);
  protected readonly metaFor = metaFor;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.svc.events$.pipe(takeUntil(this.destroy$)).subscribe(event => this.onEvent(event));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    for (const toast of this.toasts()) {
      if (toast.timeout) {
        clearTimeout(toast.timeout);
      }
    }
  }

  private onEvent(n: NotificationDTO): void {
    const pref = this.svc.prefFor(n.type);
    if (pref.muted || !pref.toastEnabled) {
      return; // sin toast tampoco hay sonido
    }
    if (this.toasts().some(t => t.n.id === n.id)) {
      return;
    }

    // Máximo 3 visibles: si llega otro, el más antiguo se despide
    const current = this.toasts().filter(t => !t.leaving);
    if (current.length >= MAX_VISIBLE_TOASTS) {
      this.startLeave(current[0]);
    }

    const toast: ActiveToast = { n, leaving: false, timeout: null };
    toast.timeout = setTimeout(() => this.startLeave(toast), TOAST_DURATION_MS);
    this.toasts.update(list => [...list, toast]);

    if (!pref.soundMuted) {
      this.svc.playDing();
    }
  }

  protected pause(toast: ActiveToast): void {
    if (toast.timeout) {
      clearTimeout(toast.timeout);
      toast.timeout = null;
    }
  }

  protected resume(toast: ActiveToast): void {
    if (!toast.leaving && !toast.timeout) {
      toast.timeout = setTimeout(() => this.startLeave(toast), TOAST_DURATION_MS);
    }
  }

  protected open(toast: ActiveToast): void {
    this.svc.markSeen(toast.n.id).subscribe({ error: () => undefined });
    if (toast.n.link) {
      void this.router.navigateByUrl(toast.n.link);
    }
    this.startLeave(toast);
  }

  protected dismiss(toast: ActiveToast, event: MouseEvent): void {
    event.stopPropagation();
    this.startLeave(toast);
  }

  private startLeave(toast: ActiveToast): void {
    if (toast.leaving) {
      return;
    }
    if (toast.timeout) {
      clearTimeout(toast.timeout);
      toast.timeout = null;
    }
    this.toasts.update(list => list.map(t => (t === toast ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      this.toasts.update(list => list.filter(t => t.n.id !== toast.n.id));
    }, LEAVE_ANIMATION_MS);
  }
}
